import styled from "styled-components";
import useMenuTitle from "../../hooks/useMenuTitle";
import { useOrientation } from "../../hooks/useOrienttation";
import { useEffect, useState } from "react";
import HeadingSection from "../maintenance/HeadingSection";
import ComChart from "../status/ComChart";
import DepalChart from "../status/DepalChart";
import PageFillgrade from "../status/CraneFillgrade/PageFillgrade";
import Dispatch from "../status/Dispatch";
import SiteServicesOrders from "./SiteServicesOrders";
import StatusMaintenance from "../status/StatusMaintenance";
import WeekMenuPreview from "../lunchMenu/WeekMenuPreview";



const ParentContainer = styled.div`
  zoom: 1;
  @media (max-width: 1440px) {
    zoom: 0.8;
  }
  display: flex;
  flex-direction: column;
  height: ${({ $height }) => $height}px;
  overflow: hidden;
  gap: 1rem;
  box-sizing: border-box;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:first-child {
    flex: 2;
  }

  &:last-child {
    flex: 3;
  }
`;

const PortraitSection = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  & > div:nth-child(1) {
    flex: 1;
  }

  & > div:nth-child(2),
  & > div:nth-child(3) {
    flex: 3;
  }
`;

const TopGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding: 1rem;
  box-sizing: border-box;
  flex: 1;
  overflow: hidden;
`;

const PortraitTopGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  padding: 1rem;
  flex: 1;
  box-sizing: border-box;
`;

const PortraitBottomGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  padding: 1rem;
  flex: 1;
  box-sizing: border-box;
`;

/* ⬇️ Bottom now has 3 boxes; left spans two of the old widths via 2fr */
const BottomGridContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem;
  box-sizing: border-box;
  flex: 1;
  overflow: hidden;
`;

const RowContainer3 = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 50rem;
  background-color: var(--backdrop-color);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-div-md);
  border-radius: 1rem;
  overflow: hidden;
`;

const MergedLeft = styled.div`
  display: grid;
  grid-template-rows: 0.6fr 0.4fr;
  width: 100%;
  height: 100%;
`;

const MergedTop = styled.div`
  display: flex;
  overflow: hidden;
`;

const MergedBottom = styled.div`
  display: grid;
  background-color: var(--backdrop-color);
  backdrop-filter: blur(10px);
  grid-template-columns: 3fr 1fr;
  gap: 1rem;
`;

const InnerBox = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  padding: 0.75rem; /* ← tiny border/margin */

  box-shadow: var(--shadow-div-sm);
  border-radius: 0.75rem;
  // overflow: hidden; /* ← trims the SVG edges cleanly */
`;

// the SVG should fill the space; let preserveAspectRatio do the centering + crop
const Svg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
`;

function ViewSiteServices() {
  useMenuTitle("Site Status");
  const orientation = useOrientation();
  const isPortrait = orientation === "portrait";

  // constants of the original artboard
  const VB_W = 7207.08;
  const VB_H = 3505.63;

  // total vertical crop as fraction (e.g. 0.20 = 20% total, 10% top + 10% bottom)
  const CROP_Y = 0.2;

  const yOffset = (VB_H * CROP_Y) / 2;
  const croppedHeight = VB_H * (1 - CROP_Y);
  const croppedViewBox = `0 ${yOffset} ${VB_W} ${croppedHeight}`;

  const PADDING_TOP_REM = 15;
  const REM_IN_PX = 7; // adjust if root font-size is different
  const paddingTopPx = PADDING_TOP_REM * REM_IN_PX;
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const updateHeight = () => {
      const header = document.getElementById("app-header");
      const headerHeight = header?.offsetHeight + paddingTopPx ?? 0;
      setHeight(window.innerHeight - headerHeight);
    };

    updateHeight(); // run on mount
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [paddingTopPx]);

  return (
    <ParentContainer $height={height}>
      <HeadingSection text="PRODUCTION" />
      {isPortrait ? (
        <PortraitSection>
          <PortraitTopGrid>
            <RowContainer3>
              <ComChart />
            </RowContainer3>
            <RowContainer3>
              <DepalChart />
            </RowContainer3>
          </PortraitTopGrid>
          <RowContainer3>
            <PageFillgrade />
          </RowContainer3>
          <RowContainer3>
            <Dispatch />
          </RowContainer3>
        </PortraitSection>
      ) : (
        <Section>
          <TopGridContainer>
            <RowContainer3>
              <ComChart />
            </RowContainer3>
            <RowContainer3>
              <DepalChart />
            </RowContainer3>
            <RowContainer3>
              <PageFillgrade />
            </RowContainer3>
          </TopGridContainer>
        </Section>
      )}

      <Section>
        <HeadingSection text="STATUS" />
        {isPortrait ? (
          <PortraitTopGrid>
            <RowContainer3>
              <SiteServicesOrders />
            </RowContainer3>
            <RowContainer3>
              {/* Keep a single StatusMaintenance in portrait */}
              <StatusMaintenance />
            </RowContainer3>
          </PortraitTopGrid>
        ) : (
          <BottomGridContainer>
            {/* ⬇️ Left: merged panel (occupies 2 old widths) */}
            <RowContainer3>
              <SiteServicesOrders />
            </RowContainer3>

            {/* ⬇️ Middle: keep a distinct box (no duplicate StatusMaintenance) */}
            <RowContainer3>
              <StatusMaintenance />
            </RowContainer3>

            {/* ⬇️ Right: Week menu stays */}
            <RowContainer3>
              <WeekMenuPreview />
            </RowContainer3>
          </BottomGridContainer>
        )}
      </Section>
    </ParentContainer>
  );
}

export default ViewSiteServices;
